import * as React from "react";
import { Loader2 } from "lucide-react";

import {
  BROKER_FIELD_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { DashboardLayoutDialogFooter } from "@/views/Dashboard/Dashboard/components/DashboardLayoutDialogFooter";
import type { CreatePnlMilestoneRequest } from "../../../../types/milestones";
import { PnlMilestoneFieldSelect } from "./PnlMilestoneFieldSelect";
import {
  PNL_MILESTONE_DIRECTION_OPTIONS,
  PNL_MILESTONE_SCOPE_OPTIONS,
  PNL_MILESTONE_TYPE_OPTIONS,
} from "./pnlMilestonesUi";

export interface PnlMilestoneCreateDialogProps {
  open: boolean;
  draft: CreatePnlMilestoneRequest | null;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onDraftChange: React.Dispatch<React.SetStateAction<CreatePnlMilestoneRequest | null>>;
  onSubmit: () => void;
}

export function PnlMilestoneCreateDialog({
  open,
  draft,
  saving,
  onOpenChange,
  onDraftChange,
  onSubmit,
}: PnlMilestoneCreateDialogProps): React.ReactElement {
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !saving) onOpenChange(false);
    if (nextOpen) onOpenChange(true);
  };

  return (
    <StrategyPineDetailDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Add milestone"
      description="Create a custom PnL milestone for the selected account."
      contentClassName="sm:max-w-lg"
      bodyClassName="flex-none overflow-visible"
      showDismissButton={false}
      footer={
        <DashboardLayoutDialogFooter onCancel={() => onOpenChange(false)}>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="shrink-0"
            disabled={saving || !draft}
            aria-busy={saving || undefined}
            onClick={onSubmit}
          >
            {saving ? (
              <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
            ) : (
              <span data-font="primary">Create</span>
            )}
          </Button>
        </DashboardLayoutDialogFooter>
      }
    >
      {!draft ? (
        <div className="flex min-h-[120px] items-center justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden />
        </div>
      ) : (
        <div className="flex flex-col gap-4" data-pnl-milestone-create-form="">
          <Field className="gap-2">
            <FieldLabel htmlFor="pnl-milestone-create-name" className={BROKER_ALERT_TITLE_CLASS}>
              Name (optional)
            </FieldLabel>
            <Input
              id="pnl-milestone-create-name"
              value={draft.name ?? ""}
              onChange={(event) =>
                onDraftChange((prev) => (prev ? { ...prev, name: event.target.value } : prev))
              }
              data-font="primary"
            />
          </Field>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <PnlMilestoneFieldSelect
              id="pnl-milestone-create-scope"
              label="Scope"
              value={draft.scope ?? "delta"}
              onValueChange={(value) =>
                onDraftChange((prev) =>
                  prev ? { ...prev, scope: value as CreatePnlMilestoneRequest["scope"] } : prev
                )
              }
              options={PNL_MILESTONE_SCOPE_OPTIONS}
            />
            <PnlMilestoneFieldSelect
              id="pnl-milestone-create-type"
              label="Type"
              value={draft.milestone_type ?? "absolute"}
              onValueChange={(value) =>
                onDraftChange((prev) =>
                  prev
                    ? { ...prev, milestone_type: value as CreatePnlMilestoneRequest["milestone_type"] }
                    : prev
                )
              }
              options={PNL_MILESTONE_TYPE_OPTIONS}
            />
            <PnlMilestoneFieldSelect
              id="pnl-milestone-create-direction"
              label="Direction"
              value={draft.direction ?? "up"}
              onValueChange={(value) =>
                onDraftChange((prev) =>
                  prev ? { ...prev, direction: value as CreatePnlMilestoneRequest["direction"] } : prev
                )
              }
              options={PNL_MILESTONE_DIRECTION_OPTIONS}
            />
          </div>

          <Field className="gap-2">
            <FieldLabel htmlFor="pnl-milestone-create-threshold" className={BROKER_ALERT_TITLE_CLASS}>
              Threshold value
              {draft.milestone_type === "percent" ? " (%)" : " (currency)"}
            </FieldLabel>
            <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
              The milestone fires when generated PnL crosses this threshold.
            </FieldDescription>
            <Input
              id="pnl-milestone-create-threshold"
              type="number"
              value={draft.threshold_value}
              onChange={(event) =>
                onDraftChange((prev) =>
                  prev ? { ...prev, threshold_value: Number(event.target.value) } : prev
                )
              }
              data-font="primary"
            />
          </Field>
        </div>
      )}
    </StrategyPineDetailDialog>
  );
}
